/**
 * useRewardData Hook
 * 
 * Clean hook for reward catalog list data management.
 * Follows rewards-mfe pattern: single useEffect, direct API calls, simple dispatch.
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import { rewardService } from '@/app/services/rewardService';
import type { RewardQueryParams } from '@/app/types/rewards';
import {
  setLoading,
  setData,
  setError,
  setFilters,
  setPage,
  setPageSize,
  clearFilters,
  invalidate as invalidateAction,
  selectRewards,
  selectRewardsTotalCount,
  selectRewardsCurrentPage,
  selectRewardsPageSize,
  selectRewardsFilters,
  selectRewardsIsLoading,
  selectRewardsError,
  selectRewardsTotalPages,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '@/app/store/rewardsSlice';

export function useRewardData() {
  const dispatch = useAppDispatch();
  
  // Select all state
  const rewards = useAppSelector(selectRewards);
  const totalCount = useAppSelector(selectRewardsTotalCount);
  const currentPage = useAppSelector(selectRewardsCurrentPage);
  const pageSize = useAppSelector(selectRewardsPageSize);
  const filters = useAppSelector(selectRewardsFilters);
  const isLoading = useAppSelector(selectRewardsIsLoading);
  const error = useAppSelector(selectRewardsError);
  const totalPages = useAppSelector(selectRewardsTotalPages);

  // Core fetch function - calls API directly, dispatches simple actions
  const fetchData = useCallback(async (params?: RewardQueryParams) => {
    // Use isLoading from state as guard instead of ref
    dispatch(setLoading(true));
    
    try {
      const response = await rewardService.queryRewards(params ?? filters);
      dispatch(setData({ rewards: response.rewards, total: response.total }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load rewards';
      dispatch(setError(errorMessage));
    }
  }, [dispatch, filters]);

  // Always fetch fresh data on mount - admin panel needs current data
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Fetch once on mount

  // Apply filters and fetch
  const applyFilters = useCallback((newFilters: Partial<RewardQueryParams>) => {
    dispatch(setFilters(newFilters));
    // Fetch with merged filters
    const mergedFilters = { ...filters, ...newFilters, offset: 0 };
    fetchData(mergedFilters);
  }, [dispatch, filters, fetchData]);

  // Change page
  const goToPage = useCallback((page: number) => {
    dispatch(setPage(page));
    fetchData({ ...filters, offset: (page - 1) * pageSize });
  }, [dispatch, filters, pageSize, fetchData]);

  // Refresh current view (unconditional fetch)
  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Clear all filters
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    fetchData({ limit: pageSize, offset: 0 });
  }, [dispatch, pageSize, fetchData]);

  // Mark list as invalidated (will refetch on next render)
  const invalidate = useCallback(() => {
    dispatch(invalidateAction());
  }, [dispatch]);

  // Change page size
  const changePageSize = useCallback((size: number) => {
    if (PAGE_SIZE_OPTIONS.includes(size as PageSizeOption)) {
      dispatch(setPageSize(size as PageSizeOption));
      fetchData({ ...filters, limit: size, offset: 0 });
    }
  }, [dispatch, filters, fetchData]);

  return {
    // Data
    rewards,
    totalCount,
    
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    
    // Filters
    filters,
    
    // Status
    isLoading,
    error,
    
    // Actions
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    invalidate,
    
    // Page size options
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}
